import { useRouter } from '@uirouter/react';
import { Users } from 'lucide-react';

import { usePublicSettings } from '@/react/portainer/settings/queries';
import { useHasEnabledAddon } from '@/react/portainer/addons/queries/useHasEnabledAddon';
import { useTeamHasEnabledAddonAccess } from '@/react/portainer/addons/queries/useTeamHasEnabledAddonAccess';

import { Widget } from '@@/Widget';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { Tooltip } from '@@/Tip/Tooltip';

import { Team, TeamMembership, TeamRole } from '../types';
import { DENY_PORTAINER_ACCESS_TOOLTIP } from '../denyPortainerAccess';
import { useDeleteTeamMutation } from '../queries/useDeleteTeamMutation';
import { useUpdateTeamMutation } from '../queries/useUpdateTeamMutation';

interface Props {
  team: Team;
  memberships: TeamMembership[];
  isAdmin: boolean;
}

export function Details({ team, memberships, isAdmin }: Props) {
  const deleteMutation = useDeleteTeamMutation();
  const updateMutation = useUpdateTeamMutation();
  const router = useRouter();
  const teamSyncQuery = usePublicSettings<boolean>({
    select: (settings) => settings.TeamSync,
  });
  const hasEnabledAddon = useHasEnabledAddon();
  const teamHasAddonAccess = useTeamHasEnabledAddonAccess(team.Id);

  const leaderCount = memberships.filter(
    (m) => m.Role === TeamRole.Leader
  ).length;

  // Only enable for teams that can reach an add-on, else they'd be redirected
  // into a gateway 403. An already-denying team can always turn it off.
  const canDenyPortainerAccess = team.DenyPortainerAccess || teamHasAddonAccess;

  return (
    <div className="row">
      <div className="col-lg-12 col-md-12 col-xs-12">
        <Widget>
          <Widget.Title title="Team details" icon={Users} />

          <Widget.Body className="no-padding">
            <table className="table">
              <tbody>
                <tr>
                  <td>Name</td>
                  <td>
                    <div className="flex gap-2">
                      {!teamSyncQuery.data && team.Name}
                      {isAdmin && (
                        <DeleteButton
                          size="xsmall"
                          onConfirmed={handleDeleteClick}
                          confirmMessage="Do you want to delete this team? Users in this team will not be deleted."
                          data-cy={`delete-team-${team.Name}`}
                        >
                          Delete this team
                        </DeleteButton>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Leaders</td>
                  <td>{!teamSyncQuery.data && leaderCount}</td>
                </tr>
                <tr>
                  <td>Total users in team</td>
                  <td>{memberships.length}</td>
                </tr>
                {isAdmin && (hasEnabledAddon || team.DenyPortainerAccess) && (
                  <tr>
                    <td>
                      Deny access to Portainer
                      <Tooltip message={DENY_PORTAINER_ACCESS_TOOLTIP} />
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Switch
                          name="denyPortainerAccess"
                          id={`team-deny-portainer-access-${team.Id}`}
                          checked={team.DenyPortainerAccess}
                          onChange={handleDenyPortainerAccessChange}
                          disabled={
                            updateMutation.isLoading || !canDenyPortainerAccess
                          }
                          data-cy={`team-denyPortainerAccessToggle-${team.Id}`}
                        />
                        {!canDenyPortainerAccess && (
                          <span className="text-xs text-[var(--text-muted-color)]">
                            Grant this team access to an add-on first.
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Widget.Body>
        </Widget>
      </div>
    </div>
  );

  async function handleDeleteClick() {
    router.stateService.go('portainer.teams');
    deleteMutation.mutate(team.Id);
  }

  function handleDenyPortainerAccessChange(denyPortainerAccess: boolean) {
    updateMutation.mutate({ id: team.Id, denyPortainerAccess });
  }
}
